// frontend/src/samkalp-faculty/mentoring/MyMentoringSlotsPage.js
//
// Faculty's own mentoring slots — open (unbooked), booked (upcoming
// 1:1s), and past. Join is only enabled inside the call's join window
// (matches mentoring_service.py's JOIN_WINDOW_BEFORE_MINUTES=5), same
// pattern as Live Sessions' go-live-window gating.
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Tab, Tabs, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PublishSlotsForm from './PublishSlotsForm';
import { listMyMentoringSlotsFaculty, cancelMentoringSlotFaculty } from '../../api/mentoringApi';

const STATUS_VARIANT = { open: 'secondary', booked: 'danger', completed: 'success', cancelled: 'dark' };
const JOIN_WINDOW_BEFORE_MS = 5 * 60 * 1000;

const canJoin = (slot) => {
    const now = Date.now();
    return now >= new Date(slot.start_utc).getTime() - JOIN_WINDOW_BEFORE_MS && now <= new Date(slot.end_utc).getTime();
};

const MyMentoringSlotsPage = ({ slug }) => {
    const navigate = useNavigate();
    const [slots, setSlots] = useState([]);
    const [showPublish, setShowPublish] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        listMyMentoringSlotsFaculty(slug).then(setSlots).catch(() => setSlots([]));
    }, [slug]);

    useEffect(() => {
        load();
        const interval = setInterval(load, 20000);
        return () => clearInterval(interval);
    }, [load]);

    const handleCancel = async (slotId) => {
        try {
            await cancelMentoringSlotFaculty(slug, slotId);
            load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not cancel this slot.');
        }
    };

    const now = Date.now();
    const openSlots = slots.filter(s => s.status === 'open');
    const bookedSlots = slots.filter(s => s.status === 'booked');
    const pastSlots = slots.filter(s => ['completed', 'cancelled'].includes(s.status) || (s.status === 'open' && new Date(s.start_utc).getTime() < now));

    const renderSlot = (s) => (
        <Card key={s.id} className="mb-2">
            <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                    <strong>{new Date(s.start_utc).toLocaleString()}</strong>
                    <div className="small text-muted">30 min &middot; {s.student_id ? `Booked` : 'Not yet booked'}</div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Badge bg={STATUS_VARIANT[s.status] || 'secondary'} className="text-uppercase">{s.status}</Badge>
                    {(s.status === 'open' || s.status === 'booked') && (
                        <Button size="sm" variant="outline-secondary" onClick={() => handleCancel(s.id)}>Cancel</Button>
                    )}
                    {s.status === 'booked' && canJoin(s) && (
                        <Button size="sm" variant="success" onClick={() => navigate(`/samkalp-ias/faculty/mentoring/${s.id}/call`)}>Join Call</Button>
                    )}
                </div>
            </Card.Body>
        </Card>
    );

    return (
        <div className="my-mentoring-slots-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">1:1 Mentoring</h4>
                <Button onClick={() => setShowPublish(o => !o)}>{showPublish ? 'Close' : '+ Publish Slots'}</Button>
            </div>

            {error && <Alert variant="danger" className="py-2 small" onClose={() => setError('')} dismissible>{error}</Alert>}

            {showPublish && (
                <Card className="mb-4 p-3">
                    <PublishSlotsForm slug={slug} onPublished={() => { setShowPublish(false); load(); }} />
                </Card>
            )}

            <Tabs defaultActiveKey="upcoming" className="mb-3">
                <Tab eventKey="upcoming" title={`Upcoming (${openSlots.length + bookedSlots.length})`}>
                    {openSlots.length === 0 && bookedSlots.length === 0 && <p className="text-muted">No upcoming slots — publish some availability above.</p>}
                    {[...bookedSlots, ...openSlots].sort((a, b) => new Date(a.start_utc) - new Date(b.start_utc)).map(renderSlot)}
                </Tab>
                <Tab eventKey="past" title={`Past (${pastSlots.length})`}>
                    {pastSlots.length === 0 && <p className="text-muted">No past slots yet.</p>}
                    {pastSlots.map(renderSlot)}
                </Tab>
            </Tabs>
        </div>
    );
};

export default MyMentoringSlotsPage;
